/**
 * @file repDocCards.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-28
 * @modified 2026-10-28
 * @description What a rep's phone card says about an invoice, a purchase
 *              order and a standing order (RepDocCard). Cam, 28 Oct 2026
 *              (rep mobile review, "All 20"): the invoice, PO and standing
 *              order cards are rebuilt the way the Sales Orders cards were —
 *              a tap on the card opens the record, the status in words,
 *              nothing sitting on anything else.
 *
 * Each function turns one search row into the card's facts: a number, a
 * title, a line under it, a date with its label, status words with a tone,
 * the money, and any flags. The words come from the row's own columns — no
 * status here that the desktop does not already show — and the dates use the
 * order card's rule (utils/repOrderCard.deliveryDateParts: "Thu 29 Oct",
 * Today / Tomorrow / Yesterday).
 *
 * Pure: no React, no fetch, today passed in so the tests can pin it.
 */

import { deliveryDateParts } from './repOrderCard';
import { londonToday } from './transportLock';
import { poStatusLabel, normalisePoStatus } from './poStatus';
import { quantityWithTonnes } from './qtyWithTonnes';

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const dayOf = (d) => {
  const s = d == null ? '' : String(d).slice(0, 10);
  return DAY.test(s) ? s : null;
};
const text = (v) => String(v == null ? '' : v).trim();
const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const PENNY = 0.005;

/** Whole days from `from` to `to` (both 'YYYY-MM-DD'). */
function daysBetween(from, to) {
  const a = Date.UTC(+from.slice(0, 4), +from.slice(5, 7) - 1, +from.slice(8, 10));
  const b = Date.UTC(+to.slice(0, 4), +to.slice(5, 7) - 1, +to.slice(8, 10));
  return Math.round((b - a) / 86400000);
}

/** "£1,234.50" — money on a card. */
export function moneyText(amount) {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency', currency: 'GBP', minimumFractionDigits: 2, maximumFractionDigits: 2
  }).format(num(amount));
}

/** "26/10" this year, "26/10/25" another — a short day for "Last … · next …". */
export function shortDay(date, today = londonToday()) {
  const d = dayOf(date);
  if (!d) return '';
  const t = dayOf(today) || d;
  const dm = `${d.slice(8, 10)}/${d.slice(5, 7)}`;
  return d.slice(0, 4) === t.slice(0, 4) ? dm : `${dm}/${d.slice(2, 4)}`;
}

/** "today", "tomorrow", "in 3 days", "yesterday", "2 days ago". */
export function relativeDay(date, today = londonToday()) {
  const d = dayOf(date);
  const t = dayOf(today);
  if (!d || !t) return '';
  const diff = daysBetween(t, d);
  if (diff === 0) return 'today';
  if (diff === 1) return 'tomorrow';
  if (diff === -1) return 'yesterday';
  return diff > 0 ? `in ${diff} days` : `${-diff} days ago`;
}

/**
 * An invoice or credit note as a card.
 *
 * Status, furthest first: voided / deleted, draft, awaiting approval, paid
 * (nothing left to pay), overdue (due before today), part paid, awaiting
 * payment. A credit note reads "Credit to use" / "Used" instead of paid.
 * The date shown is the one that matters: when it is due while it is owed,
 * otherwise the date it was raised.
 */
export function repInvoiceCard(inv = {}, today = londonToday()) {
  const credit = !!(inv.is_credit || inv.isCredit) || text(inv.transaction_type).toUpperCase() === 'CREDIT';
  const status = text(inv.status).toUpperCase();
  const total = num(inv.total);
  const paid = num(inv.amount_paid);
  const balance = inv.balance != null ? num(inv.balance) : total - paid;
  const owed = Math.abs(balance) > PENNY;
  const t = dayOf(today);
  const due = dayOf(inv.due_date);
  const overdue = !credit && owed && due && t && due < t;

  let chip;
  if (status === 'VOIDED') chip = { label: 'Voided', tone: 'muted' };
  else if (status === 'DELETED') chip = { label: 'Deleted', tone: 'muted' };
  else if (status === 'DRAFT') chip = { label: inv.awaiting_pricing ? 'Draft — being priced' : 'Draft', tone: 'muted' };
  else if (status === 'SUBMITTED') chip = { label: 'Awaiting approval', tone: 'info' };
  else if (credit) chip = owed ? { label: 'Credit to use', tone: 'info' } : { label: 'Used', tone: 'success' };
  else if (!owed || status === 'PAID') chip = { label: 'Paid', tone: 'success' };
  else if (overdue) chip = { label: 'Overdue', tone: 'error' };
  else if (paid > PENNY) chip = { label: 'Part paid', tone: 'warning' };
  else chip = { label: 'Awaiting payment', tone: 'info' };

  const showDue = !credit && owed && due && !['VOIDED', 'DELETED'].includes(status);
  const dateParts = deliveryDateParts(showDue ? due : inv.invoice_date, today);
  const when = {
    label: showDue ? (overdue ? 'Overdue since' : 'Due') : (credit ? 'Credit dated' : 'Dated'),
    text: dateParts.text,
    near: showDue && !!dateParts.when,
    alert: !!overdue
  };

  const ref = text(inv.reference);
  return {
    number: text(inv.invoice_no || inv.invoiceNo),
    kind: credit ? 'Credit note' : 'Invoice',
    title: text(inv.company_name) || 'No customer',
    place: [text(inv.account_no), ref].filter(Boolean).join(' · '),
    when,
    chip,
    total: moneyText(total),
    balance: owed && !['VOIDED', 'DELETED'].includes(status)
      ? `${moneyText(Math.abs(balance))} ${credit ? 'left to use' : 'to pay'}`
      : '',
    archived: ['VOIDED', 'DELETED'].includes(status)
  };
}

/** The tone of a PO status on a card. */
function poTone(status) {
  switch (normalisePoStatus(status)) {
    case 'CONFIRMED':
    case 'BILLED': return 'success';
    case 'SENT':
    case 'AUTHORISED': return 'info';
    case 'DRAFT': return 'muted';
    default: return 'muted';
  }
}

/**
 * A purchase order as a card: the PO number, the supplier, who it is for
 * (the customer and their order, or our stock), the delivery day, its status
 * and whether the supplier has it.
 */
export function repPurchaseOrderCard(po = {}, today = londonToday()) {
  const status = normalisePoStatus(po.status);
  const archived = status === 'DELETED' || status === 'VOIDED';
  const haulage = po.poType === 'haulage';
  const customer = text(po.customer && po.customer.name) || text(po.customerName);
  const orders = (Array.isArray(po.linkedSOs) ? po.linkedSOs : []).map(s => text(s && s.number)).filter(Boolean);
  const forText = po.selfBilled
    ? 'Self-billed — bought from the customer'
    : customer
      ? `For ${customer}${orders.length ? ` · ${orders.join(', ')}` : ''}`
      : (po.isStockPO ? 'For our stock' : (orders.length ? `For ${orders.join(', ')}` : ''));
  const date = deliveryDateParts(po.deliveryDate || po.date, today);
  const flags = [];
  if (haulage) flags.push({ key: 'haulage', label: 'Haulage order' });
  if (po.selfBilled) flags.push({ key: 'self-billed', label: 'Self-billed' });
  if (!archived && po.sentToContact === false) flags.push({ key: 'unsent', label: 'Not sent to the supplier yet' });
  return {
    number: text(po.purchaseOrderNumber),
    title: text(po.contact && po.contact.name) || 'No supplier',
    place: forText,
    when: { label: po.deliveryDate ? 'Delivery' : 'Dated', text: date.text, near: !!date.when && !!po.deliveryDate, nearLabel: date.when },
    chip: { label: poStatusLabel(status) || 'No status', tone: poTone(status) },
    total: haulage ? '' : moneyText(po.total),
    flags,
    archived
  };
}

const DAY_NAMES = ['', 'Mondays', 'Tuesdays', 'Wednesdays', 'Thursdays', 'Fridays', 'Saturdays', 'Sundays'];

/**
 * How often, in words: "Weekly · Wednesdays", "Every 2 weeks".
 */
export function standingOrderSchedule(so = {}) {
  if (so.schedule_type === 'weekly') {
    const day = DAY_NAMES[Number(so.day_of_week)] || '';
    return day ? `Weekly · ${day}` : 'Weekly';
  }
  const n = Number(so.interval_value);
  const unit = text(so.interval_type).toLowerCase().replace(/s$/, '');
  if (n > 0 && unit) return n === 1 ? `Every ${unit}` : `Every ${n} ${unit}s`;
  return 'No schedule set';
}

/**
 * The standing order's quantity in its unit: tonnes for a bulk product (its
 * sales account says so), else a plain count. Never a guessed unit.
 */
export function standingOrderQuantity(so = {}) {
  const q = Number(so.quantity);
  if (!Number.isFinite(q) || q <= 0) return '';
  const main = (Array.isArray(so.products) && so.products[0]) || {};
  const view = quantityWithTonnes(q, { accountCode: main.sales_account_code || null });
  return view.kind === 'tonnes' ? view.main : view.main || String(q);
}

/**
 * A standing order as a card: the customer on its own line, then one line in
 * words — "Weekly · Wednesdays · 18.00 T" — then "Last 26/10 · next in 2
 * days". Held and an archived product are status words, not badges laid
 * over the name.
 */
export function repStandingOrderCard(so = {}, today = londonToday()) {
  const status = text(so.status).toLowerCase() || (so.is_active === false ? 'inactive' : 'active');
  const productArchived = !!(so.main_product_archived || so.product_archived);
  let chip;
  if (status === 'held') chip = { label: 'Held', tone: 'warning' };
  else if (status === 'inactive' || so.is_active === false) chip = { label: 'Stopped', tone: 'muted' };
  else chip = { label: 'Active', tone: 'success' };

  const product = text(so.product_name) || text(so.product_code) || 'No product';
  const additive = text(so.additive_name);
  const alternatives = Math.max(0, (Array.isArray(so.products) ? so.products.length : 0) - 1);

  const last = dayOf(so.last_had_at);
  const next = dayOf(so.next_due_at);
  const lastNext = [
    last ? `Last ${shortDay(last, today)}` : 'Not had yet',
    next ? `next ${relativeDay(next, today)}` : ''
  ].filter(Boolean).join(' · ');

  const flags = [];
  if (productArchived) flags.push({ key: 'archived', label: 'Product archived — needs its replacement' });

  return {
    title: text(so.customer_name) || 'No customer',
    place: [text(so.account_no), text(so.delivery_address)].filter(Boolean).join(' · '),
    product: additive ? `${product} + ${additive}` : product,
    productCode: text(so.product_code || so.item_code),
    alternatives,
    schedule: [standingOrderSchedule(so), standingOrderQuantity(so)].filter(Boolean).join(' · '),
    lastNext,
    // The two halves for the read-only window: "26/10 · SO-D10668",
    // "in 2 days (05/11)".
    lastText: last
      ? [shortDay(last, today), text(so.last_had_reference)].filter(Boolean).join(' · ')
      : 'Not had yet',
    nextText: next
      ? `${relativeDay(next, today)} (${shortDay(next, today)})${text(so.next_due_reference) ? ` · ${text(so.next_due_reference)}` : ''}`
      : '',
    notes: text(so.notes),
    chip,
    flags
  };
}
